// EN-4743. A passport read is accepted only once the same result has come out of
// separate OCR passes.
//
// Line 1 of the MRZ (the name) has no check digit, so a read can pass every check
// digit with a wrong name. In testing a single pass returned `AZIM KMUSSA`; the
// next passes returned `AZIM MUSSA`. Requiring two identical reads held the wrong
// one back.
import type { MrzParseResult } from './mrz';

export const REQUIRED_MATCHING_READS = 2;

const readKey = (p: MrzParseResult): string =>
  [p.surname, p.givenNames, p.documentNumber, p.nationality, p.dateOfBirth, p.sex, p.dateOfExpiry].join('|');

export interface ReadAgreement {
  // Returns the parse once it has been seen `required` times, otherwise null.
  add: (parsed: MrzParseResult) => MrzParseResult | null;
  reset: () => void;
  // Reads that passed the check digits, confirmed or not.
  readonly readsPassingCheckDigits: number;
  // Different results seen. More than one means at least one read was wrong.
  readonly distinctReads: number;
}

export const createReadAgreement = (required: number = REQUIRED_MATCHING_READS): ReadAgreement => {
  let counts = new Map<string, number>();
  let passing = 0;
  return {
    add(parsed) {
      passing += 1;
      const key = readKey(parsed);
      const count = (counts.get(key) ?? 0) + 1;
      counts.set(key, count);
      return count >= required ? parsed : null;
    },
    reset() {
      counts = new Map();
      passing = 0;
    },
    get readsPassingCheckDigits() {
      return passing;
    },
    get distinctReads() {
      return counts.size;
    },
  };
};
